import { decodeJwt } from 'jose';
import type { ActivationClaims } from './ActivationClaims';
import { ClockTamperGuard } from './ClockTamperGuard';
import { persistLicenceBinding } from '@application/licence/ActivationInstallGuard';
import {
  OFFLINE_FILE_MESSAGES,
  verifyOfflineLicenceFile,
  type OfflineFileErrorCode,
} from './OfflineLicenceFile';
import type { PublicKeyResolver } from './ActivationCertificateVerifier';
import { SecureLicenceStore } from './SecureLicenceStore';
import { logger } from '@infra/telemetry/Logger';

export type OfflineImportResult =
  | { ok: true; claims: ActivationClaims }
  | { ok: false; code: OfflineFileErrorCode | 'PLUS_ANCIEN' | 'REFUSE' | 'EMPREINTE'; message: string };

/** Une restriction posée APRÈS l'export du fichier reste en vigueur (le statut signé date de l'export). */
const RESTRICTION_TOLERANCE_MS = 120_000;

export interface OfflineImportOptions {
  /** Garde d'activation (client déjà lié, portefeuille…) : renvoie un message d'erreur ou null. */
  beforePersist?: (claims: ActivationClaims) => Promise<string | null>;
  nowSec?: number;
  keyResolver?: PublicKeyResolver;
}

function iatOf(jwt: string | null): number {
  if (!jwt) return 0;
  try {
    const iat = decodeJwt(jwt).iat;
    return typeof iat === 'number' ? iat : 0;
  } catch {
    return 0;
  }
}

/**
 * Importe un fichier de licence hors ligne : vérification complète (voir verifyOfflineLicenceFile), puis
 * enregistrement du certificat comme à une activation en ligne. Le poste n'est jamais modifié si une
 * vérification échoue. Un fichier plus ancien que le certificat déjà présent est refusé (pas de retour
 * en arrière des droits).
 */
export class OfflineLicenceImportService {
  static async importFromText(
    raw: string,
    options: OfflineImportOptions = {},
  ): Promise<OfflineImportResult> {
    const fingerprint = await SecureLicenceStore.getDeviceFingerprint();
    if (!fingerprint) {
      return {
        ok: false,
        code: 'EMPREINTE',
        message: 'Empreinte du poste indisponible. Relancez l’application.',
      };
    }
    const verified = await verifyOfflineLicenceFile(raw, {
      deviceFingerprint: fingerprint,
      nowSec: options.nowSec,
      keyResolver: options.keyResolver,
    });
    if (!verified.ok) {
      logger.warn({ event: 'licence_offline_import_refused', code: verified.code });
      return verified;
    }
    const { claims, certificate, statusIat } = verified;

    const portfolio = await SecureLicenceStore.getActivationPortfolio();
    const existing = portfolio[claims.tenant_id] ?? null;
    if (existing && iatOf(existing) > claims.iat) {
      return {
        ok: false,
        code: 'PLUS_ANCIEN',
        message:
          'Ce poste possède déjà un certificat plus récent pour cette licence : le fichier est ignoré.',
      };
    }

    if (options.beforePersist) {
      const blocked = await options.beforePersist(claims);
      if (blocked) return { ok: false, code: 'REFUSE', message: blocked };
    }

    await SecureLicenceStore.upsertPortfolioCertificate(claims.tenant_id, certificate);
    await persistLicenceBinding(claims);
    const nowIso = new Date().toISOString();
    // Le fichier prouve un contact avec Nexxus à la date de l'export : le compteur « 30 jours sans contrôle » repart.
    await ClockTamperGuard.recordTrustedServerTime(new Date(statusIat * 1000).toISOString());
    await SecureLicenceStore.setLastRenewalAt(claims.tenant_id, nowIso);
    await SecureLicenceStore.setLastVerifiedAt(claims.tenant_id, nowIso);
    await SecureLicenceStore.setLastStatusCheckAt(claims.tenant_id, nowIso);

    const storedRaw = await SecureLicenceStore.getLicenceStatus(claims.tenant_id);
    if (storedRaw) {
      let since = Number.NaN;
      try {
        since = Date.parse((JSON.parse(storedRaw) as { since?: string }).since ?? '');
      } catch {
        since = Number.NaN;
      }
      // Restriction antérieure à l'export (ex. « échue » avant le paiement) : le statut « actif » signé la lève.
      if (Number.isFinite(since) && since <= statusIat * 1000 + RESTRICTION_TOLERANCE_MS) {
        await SecureLicenceStore.clearLicenceStatus(claims.tenant_id);
      }
    }
    logger.info({ event: 'licence_offline_imported', tenantId: claims.tenant_id });
    return { ok: true, claims };
  }

  static messageFor(code: OfflineFileErrorCode): string {
    return OFFLINE_FILE_MESSAGES[code];
  }
}
